import { PatientType, RefetchAfterActionComplete, useManagePatientContext } from '../lib/context/ManagePatientContext';
import { ActionItem } from '../components/Table/TableDataView';
import useIsAuditReferrer from './useIsAuditReferrer';
import useIsClientAdmin from './useIsClientAdmin';
import useIsReferrerAdmin from './useIsReferrerAdmin';
import useIsReferrer from './useIsReferrer';

type UsePatientManagementActionsParams = {
  refetchFn?: RefetchAfterActionComplete;
};

const usePatientManagementActions = (params?: UsePatientManagementActionsParams) => {
  const isAuditReferrer = useIsAuditReferrer();
  const isClientAdmin = useIsClientAdmin();
  const isReferrerAdmin = useIsReferrerAdmin();
  const isReferrer = useIsReferrer();

  const {
    onSetActionedPatient,
    openEditPatientModal,
    openResentInviteModal,
    openEditEmailModal,
    openViewNotesModal,
    openPatientAuditModal,
    openCreateCaseModal
  } = useManagePatientContext();

  const withActioned = (patient: PatientType, fn: () => void) => () => {
    onSetActionedPatient({ patient, refetchFn: params?.refetchFn });
    fn();
  };

  const generatePatientActions = (patientData: PatientType): ActionItem[] => {
    const isIncompleteRegistration = patientData.status === 'Registration Incomplete';
    const isPermanentPassword = patientData.status === 'Permanent Password';
    return [
      {
        label: 'Edit Patient Details',
        isDisabled: () => isIncompleteRegistration,
        isVisible: () => !isAuditReferrer,
        onClick: withActioned(patientData, openEditPatientModal)
      },
      {
        label: 'Create Case',
        isVisible: () => !isReferrer && !isReferrerAdmin,
        onClick: withActioned(patientData, openCreateCaseModal)
      },
      {
        label: 'Resend Invite',
        isVisible: () => !patientData.validated && !isPermanentPassword,
        onClick: withActioned(patientData, openResentInviteModal)
      },
      {
        label: 'Edit Email',
        isVisible: () => patientData.ident_prov_code === 'COGNITO',
        onClick: withActioned(patientData, openEditEmailModal)
      },
      {
        label: 'View Notes',
        onClick: withActioned(patientData, openViewNotesModal)
      },
      {
        label: 'View Audit Trail',
        isVisible: () => isClientAdmin,
        onClick: withActioned(patientData, openPatientAuditModal)
      }
    ];
  };

  return { generatePatientActions };
};

export default usePatientManagementActions;
